import { useGetPainPartKinds } from '#api/pain-part-kinds';
import { Modal } from '#molecules';
import {
  IPainPartsState,
  usePainPartsRecoil
} from '#pages/DashboardChartForm/states/painPartsState';
import { useToast } from '#providers/ToastProvider';
import ChipButtons from '#shared/ChipButtons';
import { useState } from 'react';
import Select from 'src/shared/components/select/Select';

interface AddPainPartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PainPart {
  id: string;
  name: string;
}

interface PainPartOption {
  text: string;
  value: string;
}

interface PainLevelOption {
  label: string;
  value: string;
}

export const AddPainPartModal = ({
  isOpen,
  onClose
}: AddPainPartModalProps) => {
  const { showToast } = useToast();

  const { setPainParts } = usePainPartsRecoil();

  const { data: painPartsData } = useGetPainPartKinds('수술 이력 / 통증', {
    suspense: true
  });

  const painPartsOptions: PainPartOption[] =
    painPartsData?.data
      .filter((painPart: PainPart) => painPart.name !== '없음')
      .map((painPart: PainPart) => ({
        text: painPart.name,
        value: painPart.name
      })) || [];

  const painLevelOptions: PainLevelOption[] = Array.from(
    { length: 10 },
    (_, index) => ({
      label: `${index + 1}`,
      value: `${index + 1}`
    })
  );

  // 선택된 통증 부위 리스트
  const [selectedPainParts, setSelectedPainParts] = useState<IPainPartsState[]>(
    []
  );

  // 선택된 통증 부위 이름 리스트
  const selectedPainPartNames = selectedPainParts.map(
    painPart => painPart.name || ''
  );

  const onChipButtonsChange = (value: string) => {
    setSelectedPainParts(prev => {
      const currentPainPart = prev.find(painPart => painPart.name === value);

      const kindId =
        painPartsData?.data.find(painPart => painPart.name === value)?.id || '';

      if (!currentPainPart) {
        return [...prev, { name: value, kindId }];
      }

      return prev.filter(painPart => painPart.name !== value);
    });
  };

  // 통증 정도
  const [painLevel, setPainLevel] = useState<number | undefined>(undefined);

  const onSelectChange = (value: string) => {
    setPainLevel(parseInt(value));
  };

  const onReset = () => {
    setSelectedPainParts([]);
    setPainLevel(undefined);
  };

  const onConfirm = () => {
    if (selectedPainParts.length === 0) {
      showToast({
        type: 'fail',
        message: '통증 부위를 선택해주세요.'
      });
      return;
    }

    if (!painLevel) {
      showToast({
        type: 'fail',
        message: '통증 정도를 선택해주세요.'
      });
      return;
    }

    // 통증 정도 추가
    const selectedPainPartsWithLevel = selectedPainParts.map(painPart => ({
      ...painPart,
      level: painLevel
    }));

    setPainParts(prev => [...prev, ...selectedPainPartsWithLevel]);

    onReset();

    onClose();
  };

  return (
    <>
      {isOpen && (
        <Modal onClose={onClose}>
          <div className="flex flex-col items-center w-full gap-40">
            <div className="flex flex-col w-full gap-24">
              <div className="text-[#bcb8c3] text-xl font-semibold font-['Pretendard Variable'] leading-normal tracking-tight">
                통증 추가
              </div>
              <div className="flex flex-col gap-20">
                <div className="flex flex-col gap-12">
                  <div className="flex gap-4">
                    <div className="text-[#bcb8c3] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                      운동 이름
                    </div>
                    <div className="text-[#938f9e] text-base font-normal font-['Pretendard Variable'] leading-[21px]">
                      (복수 선택 가능)
                    </div>
                  </div>

                  <ChipButtons
                    onChange={onChipButtonsChange}
                    flexWrap
                    options={painPartsOptions}
                    selectedValue={selectedPainPartNames}
                  />
                </div>
                <div className="flex flex-col gap-12">
                  <div className="text-[#bcb8c3] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                    통증 정도
                  </div>
                  <Select
                    defaultValue="선택"
                    selectContainerStyle={{ height: '53px' }}
                    options={painLevelOptions}
                    onChange={onSelectChange}
                  />
                </div>
              </div>
            </div>
            <div className="flex w-full gap-12">
              <div
                onClick={onClose}
                className="text-center flex-1 h-[45px] px-40 py-12 bg-[#2e2a3a] rounded-lg justify-center items-centerinline-flex"
              >
                <div className="text-[#bcb8c3] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                  취소
                </div>
              </div>
              <div
                onClick={onConfirm}
                className="text-center flex-1 h-[45px] px-40 py-12 bg-[#7750e9] rounded-lg justify-center items-center inline-flex"
              >
                <div className="text-[#ebe9f4] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                  확인
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
